export interface IClientCase {
  caseId: number;
  beneId: number;
  isActive: boolean;
  dependentId?: number | null;
}

export type ChecklistType = 'BENEFICIARY' | 'LEGAL_TEAM' | 'ALL';

export interface ICountryCaseTypes {
  caseType: string;
}

export interface ICaseSubtype {
  caseSubType: string;
  caseTypeId: number;
  metaDataId: number;
  subTypeGroup: number;
}

interface IChecklistDocumentType {
  docTypeId: number;
  docTypeName: string;
}

export interface IChecklistDefinition {
  id: number;
  checklistType: ChecklistType;
  migrationId: string | null;
  checklistDefinitionVersionControl?: number;
  version?: number;
  countryCaseTypeId?: number;
  documentTypeId: number;
  docGenTemplateId: number | null;
  showPrincipal: boolean;
  showSpouse: boolean | null;
  showChild: boolean | null;
  legalOnly: boolean;
  name: string;
  longTitle: string;
  description: string;
  countryCaseType?: ICountryCaseTypes;
  documentType?: IChecklistDocumentType;
  checklistDefinitionSubTypes?: null;
  checklistItems?: IChecklistItem[];
  caseSubTypes?: ICaseSubtype[];
}

export interface IChecklistItemLinkRequest {
  checklistItemId: number;
  documentId: number;
  docTypeId?: number;
}

export interface IChecklistItemDocument {
  checklistItemId: number;
  documentId: number;
  checklistItemGroupId?: number;
  document?: IDocument;
  isHidden?: boolean;
  isAutoCarryForward?: boolean;
  isAutoCarryForwardDisplayed?: boolean;
  isAutoCorrected?: boolean;
  isAutoCorrectedDisplayed?: boolean;
  autoCorrectedFrom?: string | null;
}

export interface IDocument {
  docId?: number;
  id?: string;
  documentApiKey: string;
  beneficiaryId?: number;
  docName?: string;
  documentName?: string;
  documentType?: string;
  docTypeId?: number;
  documentVisibility?: Array<{
    userType: string;
    roleType: string;
  }>;
  documentFiles?: IDocumentFiles[];
  createdBy?: number;
  createdDate?: Date;
  isLocked?: boolean;
  files?: string[];
  caseId?: number;
}

export interface IDocumentFiles {
  fileId?: number;
  fileName?: string;
  filePath?: string;
  docId?: number;
  isArchive?: boolean;
  isPrimary?: boolean;
  migrationPk?: number | null;
  batchId?: number | null;
  sharePointDocumentId?: string | null;
}

export interface IPassportInfo {
  passportId: number;
  passportNumber?: string;
  passportExpirationDate?: Date;
  passportIssuedCountry?: string;
  beneId?: number;
  batchId?: number;
  isArchive?: boolean;
  dependentId?: number;
  issuedDate?: Date;
  issuedState?: string;
  issuedCity?: string;
  hasEnoughBlanks?: boolean;
  isTemporary?: boolean;
  isBiometric?: boolean;
  passportType?: string;
  locationOfPassport?: string;
  issuedTo?: number;
  country?: string;
  isPrimary?: boolean;
  issuingAuthority?: string;
  isInactive?: boolean;
  isComplete?: boolean;
  documents?: IDocument[];
}

export interface IPassportGroup {
  fileList: File[];
}

export interface IChecklistItemGroup {
  id: number;
  checklistItemId: number;
  checklistItem?: IChecklistItem;
  passportInfoId?: number;
  passportInfo?: IPassportInfo;
  checklistItemDocuments?: IChecklistItemDocument[];
  showNewPassportGroup?: boolean;
}

interface IChecklistBeneficiary {
  beneId: number;
  companyId?: number;
  firstName: string;
  middleName: string;
  lastName: string;
  fullName: string;
  relationship?: string;
}

interface IDependent {
  dependentId: number;
  dependentFirstName?: string;
  dependentMiddleName?: string | null;
  dependentLastName?: string;
  dependentFullName?: string;
  relationship: string;
}

export interface IChecklistItemLinkRequest {
  checklistItemId: number;
  documentId: number;
  docTypeId?: number;
}

export interface IAdditionalDocumentsRequest {
  documentName: string;
  documentId: number;
  caseId: string;
  userTypes: string[];
  roleType: string;
  documentTypeId: number;
  isRemote: boolean;
  documentApiKey: string;
}

export interface IChecklistItem {
  id: number;
  beneficiaryId: number;
  beneficiary?: IChecklistBeneficiary;
  dependentId: number | null;
  dependent: IDependent | null;
  clientCaseId: number;
  clientCase: IClientCase;
  checklistDefinitionId?: number | null;
  checklistDefinition?: IChecklistDefinition | null;
  checklistItemDocuments: IChecklistItemDocument[];
  checklistItemGroups?: IChecklistItemGroup[];
  filingPackageSectionId?: number;
  createdDate?: string;
  isShowing: boolean;
  isNotApplicable?: boolean;
}

export const DOCUMENT_VISIBILITY = [
  'INTERNAL',
  'BENEFICIARY',
  'HR_CONTACT',
  'HR_LEAD',
  'NETWORK_PARTNER_L1',
  'NETWORK_PARTNER_L2',
  'NETWORK_PARTNER_L3',
];

export const DEPENDENT_DOCUMENT_VISIBILITY = ['INTERNAL', 'BENEFICIARY'];

export const DEPENDENT_DOCUMENT_VISIBILITY_GLOBAL_CASE = [
  'INTERNAL',
  'BENEFICIARY',
  'NETWORK_PARTNER_L1',
  'NETWORK_PARTNER_L2',
  'NETWORK_PARTNER_L3',
];

export interface ISignedPutRequest {
  id: string;
  filename: string;
  urls: string[];
  hashToken?: string;
}

export interface IPart {
  number: number;
  mD5: string;
}

export interface IFileInfo {
  fileName?: string;
  mD5?: string;
  multiParts?: IPart[];
}

export interface IUploadRequest {
  files: IFileInfo[];
  metadata: IDocumentMetadata;
}

export interface IDocumentMetadata {
  name: string;
  type: number;
  common: ICommonMetadata;
}

export interface ICommonMetadata {
  visibility: string[];
  companyId?: number;
  clientCaseIds?: number[];
  beneId?: number;
  dependentId?: number | null;
}

export interface IChecklistItemGroupRequest {
  documentId?: number;
  docName?: string;
  checklistItemId?: number;
  userTypes: string[];
  roleType: string;
  dependentId?: number | null;
  passportInfoIds?: number[];
}

export interface ICasePassportRequest {
  caseId: number;
  passportInfoIds: number[];
}

export interface IImmigrationDocDraft {
  immigrationDocDraftId: number;
  docId: number;
  caseId: number;
  beneId: number;
  draftType: string;
  createdBy: number;
  createdDate: string;
  createdDateOnly: string;
}
